
import React, { useState } from 'react';
import { 
  ArrowLeft, 
  Mail, 
  Phone, 
  MapPin, 
  Send, 
  CheckCircle2, 
  Globe, 
  Zap,
  Building2,
  ChevronRight,
  ShieldCheck,
  ShieldAlert,
  Headphones,
  Lock,
  MessageSquare,
  Construction
} from 'lucide-react';

import { Language, translations } from '../../translations';

interface ContactViewProps {
  onBack: () => void;
  isPublic: boolean;
  isDemo?: boolean;
  language: Language;
}

export const ContactView: React.FC<ContactViewProps> = ({ onBack, isPublic, isDemo, language }) => {
  const t = translations[language];
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch("https://formsubmit.co/ajax/hello@minervaregtech.com", {
        method: "POST",
        headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        throw new Error("Failed to send message");
      }
    } catch (err) {
      setError(language === 'fr' ? "Échec de la transmission. Veuillez réessayer ou contacter hello@minervaregtech.com directement." : "Transmission failed. Please try again or contact hello@minervaregtech.com directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-300 ${isPublic ? 'pt-20' : 'p-6'}`}>
      {isPublic && (
        <nav className="fixed top-0 w-full z-50 bg-white/80 dark:bg-slate-950/80 backdrop-blur-xl border-b border-slate-100 dark:border-slate-900">
          <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
            <button onClick={onBack} className="flex items-center space-x-2 text-slate-500 dark:text-slate-400 font-black text-xs uppercase tracking-widest hover:text-slate-900 dark:hover:text-white transition-all group">
              <div className="p-2 bg-slate-100 dark:bg-slate-900 rounded-lg group-hover:bg-blue-600 group-hover:text-white transition-all"><ArrowLeft size={16} /></div>
              <span>{t.contact.returnHome}</span>
            </button>
            <div className="flex items-center space-x-3">
              <img src="assets/logo.png" alt="Minerva Logo" className="w-8 h-8 object-contain" />
              <span className="text-lg font-black tracking-tighter dark:text-white uppercase italic">{t.contact.hub}</span>
            </div>
          </div>
        </nav>
      )}

      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-20">
          
          {/* Left: Contact Form */}
          <div className="lg:col-span-7">
            {submitted ? (
              <div className="bg-white dark:bg-slate-900 p-16 rounded-[4rem] text-center shadow-2xl border border-slate-100 dark:border-slate-800 animate-in zoom-in-95 duration-500">
                <div className="w-24 h-24 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-[2.5rem] flex items-center justify-center mx-auto mb-10 shadow-xl shadow-emerald-100/50 dark:shadow-none animate-bounce">
                  <CheckCircle2 size={48} />
                </div>
                <h2 className="text-5xl font-black text-slate-900 dark:text-white tracking-tighter mb-6">{t.contact.successTitle}</h2>
                <p className="text-slate-500 dark:text-slate-400 font-medium text-xl leading-relaxed mb-12 max-w-sm mx-auto">
                  {t.contact.successDesc}
                </p>
                <button onClick={() => setSubmitted(false)} className="bg-slate-900 dark:bg-blue-600 text-white px-12 py-5 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-slate-800 dark:hover:bg-blue-700 transition-all shadow-xl active:scale-95">
                  {t.contact.successCta}
                </button>
              </div>
            ) : (
              <div className="bg-white dark:bg-slate-900 p-12 md:p-20 rounded-[4rem] shadow-2xl border border-slate-100 dark:border-slate-800">
                <div className="mb-16">
                   <div className="inline-flex items-center space-x-2 px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-[10px] font-black uppercase mb-6 border border-blue-100 dark:border-blue-800/50">
                     {isDemo ? <Construction size={12} /> : <Headphones size={12} />}
                     <span>{isDemo ? t.contact.restricted : t.contact.tagline}</span>
                   </div>
                  <h1 className="text-6xl font-black text-slate-900 dark:text-white tracking-tighter mb-6 leading-none uppercase italic">
                    {isDemo ? t.contact.calibration : t.contact.handshake}
                  </h1>
                  <p className="text-slate-500 dark:text-slate-400 font-medium text-lg leading-relaxed">
                    {isDemo ? t.contact.demoDesc : t.contact.contactDesc}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <ContactInput label={t.contact.lead} placeholder="Julian Casablancas" name="institutional_lead" required />
                    <ContactInput label={t.contact.email} placeholder="julian@bank.com" type="email" name="email" required />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <ContactInput label={t.contact.org} placeholder="Apex Financial Ltd" name="organization" />
                    <div className="space-y-3">
                       <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.3em] ml-2">{t.contact.objective}</label>
                       <div className="relative">
                          <select name="objective" className="w-full px-6 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-blue-500/10 outline-none transition-all dark:text-white appearance-none cursor-pointer">
                             <option>{t.contact.demo}</option>
                             <option>{t.contact.enterprise}</option>
                             <option>{t.contact.quota}</option>
                             <option>{t.contact.partnership}</option>
                          </select>
                          <ChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 rotate-90 text-slate-400 pointer-events-none" size={16} />
                       </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.3em] ml-2">{t.contact.dossier}</label>
                    <textarea 
                      rows={6}
                      name="dossier_brief"
                      required
                      className="w-full px-8 py-6 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-[2.5rem] text-sm font-medium focus:ring-4 focus:ring-blue-500/10 outline-none transition-all dark:text-white resize-none"
                      placeholder={t.contact.dossierPlaceholder}
                    ></textarea>
                  </div>

                  {error && <p className="text-rose-500 text-[10px] font-black uppercase tracking-widest text-center">{error}</p>}

                  <button 
                    type="submit"
                    disabled={loading}
                    className="w-full py-6 bg-blue-600 text-white rounded-3xl font-black uppercase text-xs tracking-[0.3em] hover:bg-blue-700 transition-all shadow-2xl shadow-blue-500/30 active:scale-95 disabled:opacity-50 flex items-center justify-center space-x-3"
                  >
                    {loading ? (
                      <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                    ) : (
                      <>
                        <Lock size={18} />
                        <span>{isDemo ? t.contact.initBooking : t.contact.cta}</span>
                      </>
                    )}
                  </button>
                  {/* FormSubmit Configuration */}
                  <input type="hidden" name="_captcha" value="false" />
                  <input type="hidden" name="_subject" value={isDemo ? "Minerva RegTech: Demo Booking Request" : "Minerva RegTech: Portal Contact"} />
                </form>
              </div>
            )}
          </div>

          {/* Right: Global Infrastructure Info */}
          <div className="lg:col-span-5 space-y-12">
            <div>
               <h3 className="text-xs font-black text-blue-600 dark:text-blue-400 uppercase tracking-[0.4em] mb-10 flex items-center">
                  <Globe size={16} className="mr-3" /> {t.contact.globalInfra}
               </h3>
               
               <div className="space-y-8">
                  <OfficeCard 
                    city="Auckland" 
                    country="New Zealand (Oceania Hub)" 
                    phone="+64 210 551 464" 
                    icon={<Globe size={24} />}
                  />
                  <OfficeCard 
                    city="Bangkok" 
                    country="Thailand (APAC Operations)" 
                    address="Ocean Tower 1, 14 fl. 170/42 New Ratchadapisek Rd"
                    icon={<Zap size={24} />}
                  />
                  <OfficeCard 
                    city="Lomé" 
                    country="Togo (EMEA Compliance)" 
                    address="Akodéssewa 45 rue des gazelles"
                    phone="+228 70 66 49 64"
                    icon={<ShieldCheck size={24} />}
                  />
               </div>
            </div>

            <div className="p-10 bg-slate-900 dark:bg-slate-900 rounded-[3.5rem] text-white relative overflow-hidden shadow-2xl border border-white/5">
               <div className="relative z-10 space-y-6">
                  <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg"><ShieldAlert size={24} /></div>
                  <h4 className="text-2xl font-black tracking-tight leading-none uppercase italic">{t.contact.priority}</h4>
                  <p className="text-slate-400 text-sm font-medium leading-relaxed">
                    {t.contact.priorityDesc}
                  </p>
                  <div className="pt-4 flex flex-col space-y-3">
                     <a href="mailto:hello@minervaregtech.com" className="flex items-center space-x-3 text-blue-400 font-black uppercase text-xs tracking-widest hover:text-blue-300 transition-colors">
                        <Mail size={16} />
                        <span>hello@minervaregtech.com</span>
                     </a>
                     <div className="flex items-center space-x-3 text-slate-500 font-mono text-[10px] font-bold">
                        <MessageSquare size={16} />
                        <span>PGP: 0x82...12A4 ({t.contact.verifyRegistry})</span>
                     </div>
                  </div>
               </div>
               <div className="absolute top-0 right-0 w-48 h-48 bg-blue-600 rounded-full blur-[80px] opacity-10 translate-x-1/4 -translate-y-1/4"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ContactInput: React.FC<{ label: string, placeholder: string, type?: string, required?: boolean, name?: string }> = ({ label, placeholder, type = 'text', required, name }) => (
  <div className="space-y-3">
    <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.3em] ml-2">{label}</label>
    <input 
      type={type} 
      name={name}
      required={required}
      className="w-full px-8 py-5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl text-sm font-bold focus:ring-4 focus:ring-blue-500/10 outline-none transition-all dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-600" 
      placeholder={placeholder} 
    />
  </div>
);

const OfficeCard: React.FC<{ city: string, country: string, address?: string, phone?: string, icon: React.ReactNode }> = ({ city, country, address, phone, icon }) => (
  <div className="bg-white dark:bg-slate-900 p-10 rounded-[3.5rem] border border-slate-100 dark:border-slate-800 shadow-sm hover:shadow-xl transition-all group flex items-start space-x-8 hover:-translate-x-2">
    <div className="p-5 bg-slate-50 dark:bg-slate-800 rounded-3xl text-slate-400 dark:text-slate-600 group-hover:bg-blue-600 group-hover:text-white group-hover:shadow-lg group-hover:shadow-blue-500/30 transition-all">
      {icon}
    </div>
    <div className="space-y-2">
      <h4 className="text-2xl font-black text-slate-900 dark:text-white tracking-tighter uppercase italic">{city}</h4>
      <p className="text-[10px] font-black text-blue-600 uppercase tracking-[0.3em] mb-4">{country}</p>
      {address && <p className="text-xs text-slate-500 dark:text-slate-400 font-medium italic leading-relaxed">{address}</p>}
      {phone && <p className="text-xs font-mono font-bold text-slate-900 dark:text-slate-300 bg-slate-50 dark:bg-slate-800 px-3 py-1 rounded-lg inline-block">{phone}</p>}
    </div>
  </div>
);
